import React, { useState } from 'react';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import { rewriteIntroduction, rewriteApproach, rewriteRecommendedApproach } from '../../services/aiService';
import { generateCustomerId } from '../../services/contextService';

function IPAContentSection({ context, setContext, onSave, saving }) {
  const [rewritingIntro, setRewritingIntro] = useState(false);
  const [rewritingApproach, setRewritingApproach] = useState(false);
  const [rewritingRecommended, setRewritingRecommended] = useState(false);
  const [status, setStatus] = useState(null);

  const handleRewriteIntroduction = async () => {
    if (!context.customer) {
      setStatus({ type: 'error', message: 'Please save customer context first' });
      return;
    }

    const customerId = generateCustomerId(context.customer);

    setRewritingIntro(true);
    setStatus(null);

    try {
      const result = await rewriteIntroduction(customerId, context);

      // Update context with new introduction
      const updatedContext = { ...context, introduction: result.introduction };
      setContext(updatedContext);

      // Save updated context to sessionStorage
      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setStatus({
        type: 'success',
        message: 'Introduction rewritten successfully with AI!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to rewrite introduction: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setRewritingIntro(false);
    }
  };

  const handleRewriteApproach = async () => {
    if (!context.customer) {
      setStatus({ type: 'error', message: 'Please save customer context first' });
      return;
    }

    const customerId = generateCustomerId(context.customer);

    setRewritingApproach(true);
    setStatus(null);

    try {
      const result = await rewriteApproach(customerId, context);

      // Update context with new approach
      const updatedContext = { ...context, approach: result.approach };
      setContext(updatedContext);

      // Save updated context to sessionStorage
      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setStatus({
        type: 'success',
        message: 'Approach rewritten successfully with AI!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to rewrite approach: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setRewritingApproach(false);
    }
  };

  const handleRewriteRecommendedApproach = async () => {
    if (!context.customer) {
      setStatus({ type: 'error', message: 'Please save customer context first' });
      return;
    }

    const customerId = generateCustomerId(context.customer);

    setRewritingRecommended(true);
    setStatus(null);

    try {
      const result = await rewriteRecommendedApproach(customerId, context);

      // Update context with new recommended approach
      const updatedContext = { ...context, recommended_approach: result.recommended_approach };
      setContext(updatedContext);

      // Save updated context to sessionStorage
      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setStatus({
        type: 'success',
        message: 'Recommended approach rewritten successfully with AI!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to rewrite recommended approach: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setRewritingRecommended(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">IPA Content</h2>
      <p className="text-gray-600 text-sm mb-6">Customize the introduction, approach, and recommendations for your report. Use AI to enhance any section with customer-specific content.</p>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      {/* Introduction */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Introduction
        </label>
        <textarea
          name="introduction"
          value={context.introduction || ''}
          onChange={(e) => setContext({ ...context, introduction: e.target.value })}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter introduction text..."
        />
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleRewriteIntroduction}
            disabled={rewritingIntro || !context.customer}
            className="text-sm"
          >
            {rewritingIntro ? 'Rewriting with AI...' : '✨ Rewrite with AI'}
          </Button>
          {!context.customer && (
            <p className="text-xs text-gray-500 mt-1">
              Save customer context first to enable AI rewrite
            </p>
          )}
        </div>
      </div>

      {/* Approach */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Approach
        </label>
        <textarea
          name="approach"
          value={context.approach || ''}
          onChange={(e) => setContext({ ...context, approach: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter approach description..."
        />
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleRewriteApproach}
            disabled={rewritingApproach || !context.customer}
            className="text-sm"
          >
            {rewritingApproach ? 'Rewriting with AI...' : '✨ Rewrite with AI'}
          </Button>
          {!context.customer && (
            <p className="text-xs text-gray-500 mt-1">
              Save customer context first to enable AI rewrite
            </p>
          )}
        </div>
      </div>

      {/* Recommended Approach */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recommended Approach
        </label>
        <textarea
          name="recommended_approach"
          value={context.recommended_approach || ''}
          onChange={(e) => setContext({ ...context, recommended_approach: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter recommended approach..."
        />
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={handleRewriteRecommendedApproach}
            disabled={rewritingRecommended || !context.customer}
            className="text-sm"
          >
            {rewritingRecommended ? 'Rewriting with AI...' : '✨ Rewrite with AI'}
          </Button>
          {!context.customer && (
            <p className="text-xs text-gray-500 mt-1">
              Save customer context first to enable AI rewrite
            </p>
          )}
        </div>
      </div>

      <Button
        variant="primary"
        onClick={onSave}
        disabled={saving || !context.customer}
        className="w-full"
      >
        {saving ? 'Saving...' : 'Save IPA Content'}
      </Button>

      {!context.customer && (
        <p className="text-sm text-gray-500 mt-2">
          Save customer context first before editing IPA content
        </p>
      )}
    </div>
  );
}

export default IPAContentSection;
